/**
 * F-MKT-N4 — different rates by category on one promo code.
 *
 * The server prices the code (promo_scope.go: scopedPromoDiscount) and names
 * it (promoRatesLabel); this file only builds the editor's summary line and
 * checks the form before saving. Same wording as promoRatesLabel, but the
 * editor names every rate (the receipt label stops at three: "+N more rates").
 */

export interface DraftRateGroup {
  /** Client-only stable key, so removing a group never shifts another's state. */
  uid?: string
  percent: string
  categoryIds: string[]
  productIds: string[]
}

function joinNames(names: string[]): string {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} & ${names[1]}`
  return `${names[0]}, ${names[1]} +${names.length - 2} more`
}

let uidSeq = 0
/** A fresh client-only key for a rate group. */
export function newRateGroupUid(): string {
  uidSeq += 1
  return `rg-${uidSeq}`
}

export function formatPercent(v: number): string {
  return `${Math.round(v * 100) / 100}%`
}

/**
 * "20% Espresso Based & Tea · 10% Desserts · 15% everything else", highest
 * rate first. Groups with no rate or no named targets are left out.
 */
export function describePromoRates(
  main: { percent: number; names: string[] },
  groups: { percent: number; names: string[] }[],
  otherPercent: number | null,
): string {
  const byRate = new Map<number, string[]>()
  for (const g of [main, ...groups]) {
    if (!(g.percent > 0) || g.names.length === 0) continue
    byRate.set(g.percent, [...(byRate.get(g.percent) ?? []), ...g.names])
  }
  const parts = [...byRate.entries()].sort((a, b) => b[0] - a[0]).map(([r, names]) => `${formatPercent(r)} ${joinNames(names)}`)
  if (otherPercent != null && otherPercent > 0) parts.push(`${formatPercent(otherPercent)} everything else`)
  return parts.join(' · ')
}

/** First problem with the extra rates, or null when they can be saved. */
export function rateGroupsProblem(groups: DraftRateGroup[], otherOn: boolean, otherPercent: string): string | null {
  const ok = (s: string) => {
    const n = Number(s)
    return s.trim() !== '' && Number.isFinite(n) && n > 0 && n <= 100
  }
  for (const g of groups) {
    if (!ok(g.percent)) return 'Each extra rate needs a percent between 1 and 100.'
    if (g.categoryIds.length + g.productIds.length === 0) return 'Pick at least one category or item for each extra rate.'
  }
  if (otherOn && !ok(otherPercent)) return 'The rate for everything else needs a percent between 1 and 100.'
  return null
}
